import { useEffect, useState } from "react";
import { useParams, Link } from "@/lib/router-compat";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ImageLightbox from "@/components/ImageLightbox";
import { optimizedSrc } from "@/components/OptimizedImage";
import "@/styles/projet-description.css";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import DOMPurify from "dompurify";

interface Project {
  id: string;
  title: string;
  slug: string;
  category: string;
  location: string | null;
  cover_image: string | null;
  description: string | null;
  images: string[] | null;
  before_image: string | null;
  after_image: string | null;
  year: string | null;
  surface: string | null;
  budget: string | null;
  client_name: string | null;
  client_type: string | null;
  tagline: string | null;
  cover_position: string | null;
}

const ProjetDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: proj }, { data: all }] = await Promise.all([
        supabase.from("projects").select("*").eq("slug", slug!).eq("published", true).single(),
        supabase.from("projects").select("id, title, slug, cover_image, category, location, description, images, before_image, after_image, year, surface, budget, client_name, client_type, tagline").eq("published", true).order("display_order"),
      ]);
      setProject(proj as Project | null);
      setAllProjects((all as Project[]) ?? []);
      if (proj) analytics.projectView(slug!);
      setLoading(false);
      setActiveImage(0);
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="py-32 text-center">
          <p className="text-muted-foreground font-body">Chargement...</p>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <SEOHead title="Projet introuvable - JIIA Studio" description="Ce projet n'existe pas ou n'est plus disponible." />
        <div className="py-32 text-center">
          <h1 className="font-heading text-3xl mb-4">Projet introuvable</h1>
          <Link to="/projets" className="text-sm font-body uppercase tracking-widest text-accent hover:text-foreground transition-colors">
            ← Retour aux projets
          </Link>
        </div>
      </Layout>
    );
  }

  const allImages = [
    ...(project.cover_image ? [project.cover_image] : []),
    ...(project.images ?? []),
  ];

  const currentIndex = allProjects.findIndex((p) => p.id === project.id);
  const prevProject = currentIndex > 0 ? allProjects[currentIndex - 1] : null;
  const nextProject = currentIndex < allProjects.length - 1 ? allProjects[currentIndex + 1] : null;

  const metaItems = [
    { label: "Année", value: project.year },
    { label: "Lieu", value: project.location },
    { label: "Surface", value: project.surface },
    { label: "Budget", value: project.budget },
    { label: "Client", value: project.client_name },
    { label: "Catégorie", value: project.client_type },
  ].filter((m) => m.value);

  const isHtml = project.description?.includes("<p>") || project.description?.includes("<h");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.title,
    description: project.description?.replace(/<[^>]*>/g, "").slice(0, 200),
    creator: { "@type": "Organization", name: "JIIA Studio" },
    image: project.cover_image,
    locationCreated: project.location ? { "@type": "Place", name: project.location } : undefined,
  };

  return (
    <Layout>
      <SEOHead
        title={`${project.title} - JIIA Studio | Architecture d'intérieur`}
        description={project.description?.replace(/<[^>]*>/g, "").slice(0, 160) || `Découvrez le projet ${project.title} réalisé par JIIA Studio.`}
        canonical={`/projets/${project.slug}`}
        ogImage={project.cover_image || undefined}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Projets", path: "/projets" },
          { name: project.title, path: `/projets/${project.slug}` },
        ]}
      />

      <section className="pt-24 md:pt-32 pb-12">
        <div className="container">
          {/* Back link */}
          <AnimatedSection>
            <Link
              to="/projets"
              className="inline-flex items-center gap-2 text-sm font-body uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Tous les projets
            </Link>
          </AnimatedSection>

          {/* Hero: cover image + project info side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
            {/* Cover image */}
            {allImages.length > 0 && (
              <AnimatedSection delay={0.1}>
                <div className="aspect-square overflow-hidden rounded-sm bg-secondary">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeImage}
                      src={optimizedSrc(allImages[activeImage] ?? "", 800)}
                      alt={`${project.title} - Image ${activeImage + 1}`}
                      className="w-full h-full object-cover cursor-zoom-in"
                      style={{ objectPosition: project.cover_position || 'center center' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      onClick={() => setLightboxOpen(true)}
                    />
                  </AnimatePresence>
                </div>
                {allImages.length > 1 && (
                  <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                    {allImages.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveImage(i)}
                        aria-label={`Voir l'image ${i + 1} de ${project.title}`}
                        className={`flex-shrink-0 h-20 md:h-24 w-fit rounded-sm border-2 transition-colors bg-secondary/40 ${
                          i === activeImage ? "border-accent" : "border-transparent opacity-70 hover:opacity-100"
                        }`}
                      >
                        <img src={img} alt={`${project.title} - miniature ${i + 1}`} className="block h-full w-auto max-w-none rounded-[2px] object-contain" loading="lazy" decoding="async" />
                      </button>
                    ))}
                  </div>
                )}
              </AnimatedSection>
            )}

            {/* Project info */}
            <AnimatedSection delay={0.2}>
              <div className="flex flex-col justify-center">
                {project.slug && (
                  <h2 className="text-xs font-body uppercase tracking-[0.25em] text-accent mb-3">
                    {project.slug
                      .split("-")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")}
                  </h2>
                )}
                <h1 className="font-heading text-3xl md:text-5xl font-light mb-8">{project.title}</h1>

                {/* Metadata grid */}
                {metaItems.length > 0 && (
                  <div className="space-y-3 mb-8">
                    {metaItems.map((item) => (
                      <div key={item.label} className="flex items-baseline gap-3">
                        <span className="text-xs font-body uppercase tracking-widest text-muted-foreground min-w-[80px]">
                          {item.label}
                        </span>
                        <span className="text-sm font-body text-foreground">{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tagline */}
                {project.tagline && (
                  <div className="border-l-2 border-accent pl-4 mb-8">
                    <p className="font-heading text-lg md:text-xl italic text-foreground leading-relaxed">
                      {project.tagline}
                    </p>
                  </div>
                )}

                {/* Description */}
                {project.description && (
                  <>
                    {isHtml ? (
                      <div
                        className="projet-description font-body text-muted-foreground leading-relaxed space-y-4"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(project.description) }}
                      />
                    ) : (
                      <div className="space-y-4">
                        {project.description.split("\n\n").map((paragraph, i) => (
                          <p key={i} className="text-muted-foreground font-body leading-relaxed whitespace-pre-line">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </AnimatedSection>
          </div>

          {/* Before / After */}
          {project.before_image && project.after_image && (
            <AnimatedSection delay={0.2}>
              <div className="mb-16">
                <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">Avant / Après</p>
                <BeforeAfterSlider
                  beforeImage={project.before_image}
                  afterImage={project.after_image}
                />
              </div>
            </AnimatedSection>
          )}

          {/* Related projects */}
          {(() => {
            const related = allProjects
              .filter((p) => p.category === project.category && p.id !== project.id)
              .slice(0, 3);
            if (related.length === 0) return null;
            return (
              <AnimatedSection delay={0.1}>
                <div className="mb-16">
                  <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">Autres projets</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {related.map((rp, i) => (
                      <AnimatedSection key={rp.id} delay={i * 0.1}>
                        <Link to={`/projets/${rp.slug}`} className="group block">
                          <div className="aspect-[4/3] overflow-hidden rounded-sm mb-3">
                            {rp.cover_image ? (
                              <img
                                src={optimizedSrc(rp.cover_image, 400)}
                                alt={rp.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className="w-full h-full bg-secondary flex items-center justify-center">
                                <p className="text-muted-foreground text-xs font-body">Image à venir</p>
                              </div>
                            )}
                          </div>
                          <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-1">
                            {rp.category}{rp.location ? ` · ${rp.location}` : ""}
                          </p>
                          <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{rp.title}</h3>
                        </Link>
                      </AnimatedSection>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            );
          })()}

          {/* Navigation prev/next */}
          <div className="py-12 border-t border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 sm:gap-4">
            {prevProject ? (
              <Link
                to={`/projets/${prevProject.slug}`}
                className="group flex items-center gap-3 text-base sm:text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors max-w-full sm:max-w-[45%] py-2 px-2 sm:px-0 sm:py-0"
              >
                <ArrowLeft className="h-5 sm:h-4 w-5 sm:w-4 flex-shrink-0 group-hover:-translate-x-1 transition-transform" />
                <span className="truncate">{prevProject.title}</span>
              </Link>
            ) : <div />}
            {nextProject ? (
              <Link
                to={`/projets/${nextProject.slug}`}
                className="group flex items-center gap-3 text-base sm:text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors max-w-full sm:max-w-[45%] sm:ml-auto py-2 px-2 sm:px-0 sm:py-0"
              >
                <span className="truncate">{nextProject.title}</span>
                <ArrowRight className="h-5 sm:h-4 w-5 sm:w-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && allImages.length > 0 && (
          <ImageLightbox
            images={allImages}
            activeIndex={activeImage}
            onClose={() => setLightboxOpen(false)}
            onNavigate={(i) => setActiveImage(i)}
            alt={project.title}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default ProjetDetail;
